'use client';

import { useEffect, useState } from 'react';
import { isSe3DisabledParam, isSe3EnabledParam } from 'utils/bootstrapHelpers';

/**
 * Detects the active `se3` editorial Builder variant from *inside* a registered
 * Builder.io component.
 *
 * Builder-registered components (GetEquityBubble, ClassicSectionNav,
 * ClassicInvestSidebar, ClassicTwoColumnLayout, …) are rendered by
 * `<BuilderComponent>` and never receive the resolved `variantKey` as a prop.
 * `BuilderLayoutClient` exposes the decision two ways that this hook reads, in
 * priority order:
 *
 *   1. The `data-builder-editorial-reskin="true"` attribute it stamps on the
 *      `[data-cy="builder-layout"]` root when `effectiveVariantKey === 'se3'`.
 *      This is the authoritative signal — it already folds in the PostHog /
 *      requested-variant resolution — but it's only readable in the DOM after
 *      mount, so it's gated behind a hydration-safe effect.
 *   2. A bare `?se3`, `?se3=1` (or `?variant=se3` / `?variantKey=se3`) query param fallback,
 *      so stage QA links and the Builder editor preview light up immediately
 *      without depending on the ancestor attribute.
 *
 * Returns `false` during SSR and the first client paint, then flips to the real
 * value after hydration. Consumers MUST treat a `false` first render as the
 * control path so the server HTML matches (no hydration mismatch) — every call
 * site already gates its se3 branch behind `useIsHydrated()` / this hook's
 * post-mount update.
 *
 * Control (no se3) is unaffected: the attribute is absent and the query param
 * is not set, so this returns `false` and every consumer renders its existing
 * markup byte-for-byte unchanged.
 *
 * @returns {boolean} true when the se3 editorial reskin is active.
 */
export default function useIsEditorialReskin() {
  const [isReskin, setIsReskin] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const compute = () => {
      // Read the live URL directly. `useSearchParams()` from next/navigation can
      // return empty inside Builder-rendered / dynamically-imported components,
      // so the ?se3 override was being missed even when present in the URL.
      const params = new URLSearchParams(window.location.search);
      const se3 = params.get('se3');
      if (isSe3DisabledParam(se3)) return false;
      if (
        isSe3EnabledParam(se3) ||
        params.get('variant') === 'se3' ||
        params.get('variantKey') === 'se3'
      ) {
        return true;
      }
      // Organic: the reskin attribute is stamped by BuilderLayoutClient on the
      // ancestor — may not exist yet on the first effect run, hence the rAF
      // re-check below.
      return Boolean(document.querySelector('[data-builder-editorial-reskin="true"]'));
    };

    const update = () => setIsReskin(compute());

    update();
    const raf = requestAnimationFrame(update);

    // The `data-builder-editorial-reskin` attribute is stamped by
    // BuilderLayoutClient only once `effectiveVariantKey === 'se3'` resolves —
    // and that includes the ASYNC PostHog variant, so on the flag path (no
    // ?se3 param) the attribute can appear AFTER this hook's mount + rAF. A
    // one-shot read leaves late-mounting consumers (notably the sticky
    // BuilderClassicSectionNav on scroll) stuck on the control styling. Observe
    // the attribute so the reskin value flips to true when it's stamped
    // (Atombeam offering nav QA).
    let observer;
    if (typeof MutationObserver === 'function') {
      observer = new MutationObserver(update);
      observer.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-builder-editorial-reskin'],
      });
    }

    return () => {
      cancelAnimationFrame(raf);
      if (observer) observer.disconnect();
    };
  }, []);

  return isReskin;
}
